import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import DemoBlockModal from '../components/DemoBlockModal';
import { isDemoUser } from '../utils/demoGuard';
import './ERDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

// Use config values
const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;
const TABLE_NAME = 'OP_PY';

// Formatter functions
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

function OPDashboardMain({ activeView, setActiveView }) {
  const { user, getAuthHeader } = useAuth();
  const [showDemoBlock, setShowDemoBlock] = React.useState(false);
  const [metrics, setMetrics] = useState({
    total_patients: 0,
    total_visits: 0,
    total_cost: 0,
    avg_risk_score: 0
  });
  const [data, setData] = useState({
    monthlyVisits: [],
    monthlyCost: [],
    topHcpcs: [],
    topFacilities: [],
    topDiagnosis: [],
    topCCSR: [],
    topChronicConditions: [],
    byCohort: [],
    byRiskGroup: []
  });
  const [viewMode, setViewMode] = useState('visits'); // 'visits' or 'cost'
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [filters, setFilters] = useState({
    'BENE_MBI_ID': ['All'],
    'PRNCPL_DGNS_CD': ['All'],
    'CLM_LINE_HCPCS_CD': ['All'],
    'CLM_LINE_PROD_REV_CTR_CD': ['All'],
    'CLM_BLG_PRVDR_NPI_NUM': ['All'],
    'CLM_BLG_PRVDR_OSCAR_NUM': ['All'],
    'fac_name': ['All'],
    'yr_mo': ['All'],
    'ccsr': ['All'],
    'CHRONIC': ['All'],
    'BENE_1ST_NAME': ['All'],
    'BENE_LAST_NAME': ['All'],
    'Gender': ['All'],
    'COHORT': ['All']
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  
  // Re-sort data when viewMode changes
  useEffect(() => {
    if (data.topFacilities.length > 0) {
      const sortDescending = (dataArray) => {
        return [...dataArray].sort((a, b) => {
          const aVal = viewMode === 'cost' ? a.cost_value : a.value;
          const bVal = viewMode === 'cost' ? b.cost_value : b.value;
          return bVal - aVal;
        });
      };
      
      setData(prevData => ({
        ...prevData,
        topHcpcs: sortDescending(prevData.topHcpcs),
        topFacilities: sortDescending(prevData.topFacilities),
        topDiagnosis: sortDescending(prevData.topDiagnosis),
        topCCSR: sortDescending(prevData.topCCSR),
        topChronicConditions: sortDescending(prevData.topChronicConditions),
        byCohort: sortDescending(prevData.byCohort)
        // Note: byRiskGroup is not re-sorted, it stays in ascending order by risk score
      }));
    }
  }, [viewMode]);

  const handleFilterChange = (field, values) => {
    console.log(`Filter changed - Field: ${field}, Values:`, values);
    setFilters(prev => ({ ...prev, [field]: values }));
  };

  const handleResetFilters = () => {
    setFilters({
      'BENE_MBI_ID': ['All'],
      'PRNCPL_DGNS_CD': ['All'],
      'CLM_LINE_HCPCS_CD': ['All'],
      'CLM_LINE_PROD_REV_CTR_CD': ['All'],
      'CLM_BLG_PRVDR_NPI_NUM': ['All'],
      'CLM_BLG_PRVDR_OSCAR_NUM': ['All'],
      'fac_name': ['All'],
      'yr_mo': ['All'],
      'ccsr': ['All'],
      'CHRONIC': ['All'],
      'BENE_1ST_NAME': ['All'],
      'BENE_LAST_NAME': ['All'],
      'Gender': ['All'],
      'COHORT': ['All']
    });
  };

  const buildFilterParams = () => {
    let params = `project=${PROJECT_ID}&dataset=${DATASET_NAME}&table=${TABLE_NAME}`;
    
    // Add filter parameters if not 'All'
    Object.entries(filters).forEach(([field, values]) => {
      if (!values.includes('All') && values.length > 0) {
        const filterValue = values.join(',');
        params += `&${field.toLowerCase()}=${encodeURIComponent(filterValue)}`;
      }
    });
    
    return params;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const params = buildFilterParams();
      
      console.log('Fetching OP combined data with params:', params);
      console.log('API Base URL:', API_BASE_URL);
      
      // Fetch combined OP data
      const combinedRes = await axios.get(`${API_BASE_URL}/api/op/combined?${params}`, { 
        timeout: 180000
      });
      
      console.log('OP Combined Response:', combinedRes.data);
      
      const responseData = combinedRes.data.data;
      
      // Update metrics
      setMetrics({
        ...responseData.metrics,
        avg_risk_score: responseData.metrics.avg_risk_score || 0
      });
      
      // Fetch beneficiaries with limit=15
      try {
        const beneficiariesRes = await axios.get(`${API_BASE_URL}/api/op/beneficiaries?${params}&limit=15`, { 
          timeout: 60000
        });
        setBeneficiaries(beneficiariesRes.data.beneficiaries || []);
      } catch (benefError) {
        console.error('Error loading beneficiaries:', benefError);
        setBeneficiaries([]);
      }
      
      const replaceNullNames = (dataArray) => {
        return dataArray.map(item => ({
          ...item,
          name: item.name === null || item.name === undefined || item.name === '' ? 'Unknown' : item.name
        }));
      };
      
      // Calculate totals for percentages
      const totalVisits = responseData.metrics.total_visits || 1;
      const totalCost = responseData.metrics.total_cost || 1;
      
      const addPercentages = (dataArray) => {
        return dataArray.map(item => ({
          ...item,
          percentage: ((item.value / totalVisits) * 100).toFixed(1),
          cost_percentage: ((item.cost_value / totalCost) * 100).toFixed(1)
        }));
      };
      
      // Sort risk score grouping by name (assumes format like "0-1", "1-1.5", etc.)
      const sortRiskScoreAscending = (dataArray) => {
        return [...dataArray].sort((a, b) => {
          const aNum = parseFloat(a.name.split('-')[0]);
          const bNum = parseFloat(b.name.split('-')[0]);
          return aNum - bNum;
        });
      };
      
      setData({
        monthlyVisits: responseData.monthly_visits || [],
        monthlyCost: responseData.monthly_cost || [],
        topHcpcs: addPercentages(replaceNullNames(responseData.by_hcpcs || [])),
        topFacilities: addPercentages(replaceNullNames(responseData.by_facility || [])),
        topDiagnosis: addPercentages(replaceNullNames(responseData.by_diagnosis || [])),
        topCCSR: addPercentages(replaceNullNames(responseData.by_ccsr || [])),
        topChronicConditions: addPercentages(replaceNullNames(responseData.by_chronic_condition || [])),
        byCohort: addPercentages(replaceNullNames(responseData.by_cohort || [])),
        byRiskGroup: sortRiskScoreAscending(addPercentages(replaceNullNames(responseData.by_risk_group || [])))
      });
      
      console.log('OP Data loaded successfully!');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching OP data:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert('Failed to load OP data. Check console for details. Error: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    if (isDemoUser(user)) { setShowDemoBlock(true); return; }
    try {
      const params = buildFilterParams();
      const response = await axios.get(`${API_BASE_URL}/api/op/beneficiaries?${params}&limit=0`, {
        timeout: 180000
      });
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        const allBeneficiaries = response.data.data;
        const headers = Object.keys(allBeneficiaries[0]);
        const csvData = allBeneficiaries.map(row => 
          headers.map(header => row[header] || '')
        );
        
        const csvContent = [
          headers.join(','),
          ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'op_visits.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="page-with-sidebar">
        <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
        <div className="dashboard">
          <div className="metrics-row" style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '30px'}}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="metric-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}>
                <div style={{height: '14px', width: '80px', backgroundColor: '#d1d5db', borderRadius: '4px', marginBottom: '8px'}}></div>
                <div style={{height: '28px', width: '100px', backgroundColor: '#d1d5db', borderRadius: '4px'}}></div>
              </div>
            ))}
          </div>
          <div style={{padding: '0px 15px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
            {[1, 2].map((i) => (
              <div key={i} className="chart-card" style={{
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${(i + 4) * 0.1}s`,
                height: '350px',
                background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
                backgroundSize: '200% 100%'
              }}></div>
            ))}
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="page-with-sidebar">
      {showDemoBlock && <DemoBlockModal onClose={() => setShowDemoBlock(false)} />}
      <Sidebar filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />
      <div className="dashboard">
      
      <div style={{animation: 'fadeIn 0.5s ease-in-out'}}>
        {/* Summary Statistics - 4 Metrics */}
        <div className="metrics-row" style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '30px'}}>
          <div className="metric-card">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <span style={{fontSize: '24px'}}>👥</span>
              <div>
                <div className="metric-label">TOTAL BENES</div>
                <div className="metric-value">{formatNumber(metrics.total_patients)}</div>
              </div>
            </div>
          </div>
          <div className="metric-card">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <span style={{fontSize: '24px'}}>📊</span>
              <div>
                <div className="metric-label">AVG RISK SCORE</div>
                <div className="metric-value">{formatRiskScore(metrics.avg_risk_score)}</div>
              </div>
            </div>
          </div>
          <div className="metric-card">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <span style={{fontSize: '24px'}}>🏢</span>
              <div>
                <div className="metric-label">TOTAL VISITS</div>
                <div className="metric-value">{formatNumber(metrics.total_visits)}</div>
              </div>
            </div>
          </div>
          <div className="metric-card">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <span style={{fontSize: '24px'}}>💰</span>
              <div>
                <div className="metric-label">TOTAL COST</div>
                <div className="metric-value">{formatCurrency(metrics.total_cost)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Monthly Line Charts */}
      <div style={{padding: '0px 15px', marginBottom: '20px'}}>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
          <div className="chart-card" style={{padding: '15px'}}>
            <h3 style={{margin: '0 0 15px 0'}}>Monthly OP Claims</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.monthlyVisits} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="yr_mo" style={{fontSize: '12px'}} />
                <YAxis style={{fontSize: '12px'}} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} contentStyle={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}} />
                <Line type="monotone" dataKey="visits" stroke="#0369a1" strokeWidth={2} dot={{r: 4}} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" style={{padding: '15px'}}>
            <h3 style={{margin: '0 0 15px 0'}}>Monthly OP Cost</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.monthlyCost} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="yr_mo" style={{fontSize: '12px'}} />
                <YAxis style={{fontSize: '12px'}} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip cursor={{ fill: '#e0f2fe' }} contentStyle={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}} formatter={(value) => formatCurrency(value)} />
                <Line type="monotone" dataKey="cost" stroke="#0284c7" strokeWidth={2} dot={{r: 4}} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Toggle for Visits/Cost */}
      <div style={{display: 'flex', justifyContent: 'center', marginBottom: '20px', gap: '10px'}}>
        <button
          onClick={() => setViewMode('visits')}
          style={{
            padding: '10px 30px',
            backgroundColor: viewMode === 'visits' ? '#0369a1' : '#e5e7eb',
            color: viewMode === 'visits' ? 'white' : '#1f2937',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s'
          }}
        >
          Visits
        </button>
        <button
          onClick={() => setViewMode('cost')}
          style={{
            padding: '10px 30px',
            backgroundColor: viewMode === 'cost' ? '#0369a1' : '#e5e7eb',
            color: viewMode === 'cost' ? 'white' : '#1f2937',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s'
          }}
        >
          Cost
        </button>
      </div>

      {/* Bar Charts - 7 Charts */}
      <div className="charts-grid">
        {/* Facility - Horizontal */}
        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Top 15 Facilities</h3>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={data.topFacilities} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <YAxis type="category" dataKey="name" width={200} style={{fontSize: '11px'}} interval={0} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '11px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Principle Diagnosis - Horizontal */}
        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Top 15 Principle Diagnoses</h3>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={data.topDiagnosis} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <YAxis type="category" dataKey="name" width={100} style={{fontSize: '12px'}} interval={0} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '12px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* CCSR - Horizontal */}
        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Top 15 CCSR</h3>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={data.topCCSR} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <YAxis type="category" dataKey="name" width={180} style={{fontSize: '11px'}} interval={0} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '11px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chronic Conditions - Horizontal */}
        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Top 15 Chronic Conditions</h3>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={data.topChronicConditions} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <YAxis type="category" dataKey="name" width={150} style={{fontSize: '12px'}} interval={0} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '12px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cohort - Vertical */}
        <div className="chart-card" style={{padding: '15px'}}>
          <h3 style={{margin: '0 0 15px 0'}}>By Cohort</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.byCohort} margin={{ top: 5, right: 20, left: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{fontSize: '12px'}} />
              <YAxis tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '11px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Score Grouping - Vertical */}
        <div className="chart-card" style={{padding: '15px'}}>
          <h3 style={{margin: '0 0 15px 0'}}>By Risk Score Grouping</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.byRiskGroup} margin={{ top: 5, right: 20, left: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{fontSize: '12px'}} />
              <YAxis tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="top" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '12px'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* HCPCS Code - Horizontal */}
        <div className="chart-card" style={{padding: '15px 0px 15px 0px'}}>
          <h3 style={{margin: '0 0 15px 20px'}}>Top 15 HCPCS Codes</h3>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={data.topHcpcs} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={viewMode === 'cost' ? (v) => `$${(v/1000).toFixed(0)}k` : undefined} />
              <YAxis type="category" dataKey="name" width={100} style={{fontSize: '12px'}} interval={0} />
              <Tooltip cursor={{ fill: '#e0f2fe' }} content={({active, payload}) => {
                if (active && payload && payload.length) {
                  const pct = viewMode === 'cost' ? payload[0].payload.cost_percentage : payload[0].payload.percentage;
                  return (
                    <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '8px', borderRadius: '4px', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                      <p style={{margin: 0, fontWeight: 'bold'}}>{payload[0].payload.name}</p>
                      <p style={{margin: '4px 0 0 0'}}>
                        {viewMode === 'cost' ? formatCurrency(payload[0].value) : formatNumber(payload[0].value)} ({pct}%)
                      </p>
                    </div>
                  );
                }
                return null;
              }} />
              <Bar dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} fill="#0284c7">
                <LabelList dataKey={viewMode === 'cost' ? 'cost_value' : 'value'} position="insideRight" 
                  formatter={(v) => viewMode === 'cost' ? `$${(v/1000).toFixed(0)}k` : formatNumber(v)} 
                  style={{fontSize: '12px', fill: 'white'}} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="table-section" style={{marginTop: '30px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <h2 style={{margin: 0}}>OP Visits ({beneficiaries.length.toLocaleString()} records)</h2>
          <button 
            onClick={downloadCSV}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0369a1',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#0369a1'}
          >
            Download CSV
          </button>
        </div>
        <div style={{overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr style={{backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                {beneficiaries.length > 0 && Object.keys(beneficiaries[0]).map((column) => (
                  <th key={column} style={{padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap'}}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {beneficiaries.map((row, index) => (
                <tr key={`${row.BENE_MBI_ID}-${row.CUR_CLM_UNIQ_ID}-${index}`} style={{borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'}}>
                  {Object.keys(row).map((column) => (
                    <td key={column} style={{padding: '12px', fontSize: '13px', color: '#1f2937', whiteSpace: 'nowrap'}}>
                      {row[column] !== null && row[column] !== undefined ? String(row[column]) : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}

export default OPDashboardMain;
